/**
 * WalletDeposit — Phase-2 currency-first deposit.
 *
 * Currency tabs across the top: BDT (Nagad, active), USDT (NOWPayments), INR + PKR
 * disabled with a "Coming soon" pill. `?currency=CODE` in the URL pre-selects
 * the tab (comes from the wallet main page's Deposit button per row).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, Coins, Copy, CreditCard, Gift, Landmark, LifeBuoy, MessageSquare,
} from "lucide-react";

import api from "../lib/api";
import { extractError } from "../lib/formatError";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui-r1/Button";
import Input from "../components/ui-r1/Input";
import { Card, CardBody } from "../components/ui-r1/Card";
import { Badge } from "../components/ui-r1/Badge";
import {
  CURRENCIES, DEPOSIT_ENABLED, DEPOSIT_CHIPS_BY_CURRENCY, formatAmount,
} from "../lib/currency";

const BDT_CHIPS = DEPOSIT_CHIPS_BY_CURRENCY.BDT;
const USDT_CHIPS = DEPOSIT_CHIPS_BY_CURRENCY.USDT;

export default function WalletDeposit() {
  const { refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Currency tab. Priority: ?currency=CODE > default to first enabled.
  const initialCurrency = useMemo(() => {
    const q = searchParams.get("currency");
    if (q && CURRENCIES.some((c) => c.code === q)) return q;
    return "BDT";
  }, [searchParams]);
  const [currency, setCurrency] = useState(initialCurrency);

  const [methods, setMethods] = useState(null);
  const [error, setError] = useState(null);
  const [bdt, setBdt] = useState({ amount: 1000, creating: false, redirecting: false });
  const [usdt, setUsdt] = useState({ amount: 50, creating: false, invoice: null });
  const [nagadReturn, setNagadReturn] = useState(null);
  const [bonusFx, setBonusFx] = useState(null);

  // load methods + handle Payfly return bounce
  useEffect(() => {
    api.get("/wallet/methods").then(({ data }) => {
      setMethods(data);
      const qs = new URLSearchParams(window.location.search);
      const returningOrder = qs.get("nagad");
      if (returningOrder) {
        setCurrency("BDT");
        setNagadReturn({
          order_id: returningOrder,
          status: qs.get("status") || "pending",
          polling: true, credited: false, attempts: 0,
        });
      }
    }).catch((e) => setError(extractError(e)));
  }, []);

  // keep URL in sync with tab clicks
  const selectCurrency = useCallback((code) => {
    setCurrency(code);
    const next = new URLSearchParams(searchParams);
    next.set("currency", code);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Poll Nagad status after Payfly return
  useEffect(() => {
    if (!nagadReturn?.polling) return;
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/wallet/nagad/status/${nagadReturn.order_id}`);
        if (data.credited) {
          setNagadReturn((r) => ({ ...r, ...data, polling: false }));
          toast.success(
            `Deposit credited: ৳${(data.actual_received_amount_bdt || data.amount_bdt || 0).toLocaleString()} → BDT wallet`,
            { duration: 6000 }
          );
          if (data.welcome_bonus_credited && data.welcome_bonus_amount_usd > 0) {
            setBonusFx({ amount: data.welcome_bonus_amount_usd });
            setTimeout(() => setBonusFx(null), 4500);
          }
          await refresh();
          try { window.history.replaceState({}, "", "/wallet/deposit?currency=BDT"); } catch { /* ignore */ }
          return;
        }
        const finalStates = ["expired", "cancelled_by_user", "suspended", "hold"];
        if (finalStates.includes(data.status)) {
          setNagadReturn((r) => ({ ...r, ...data, polling: false }));
          toast.error(`Deposit ${data.status.replace(/_/g, " ")}. Please try again.`, { duration: 6000 });
          return;
        }
      } catch (e) {
        console.debug("[WalletDeposit] nagad poll transient:", e);
      }
      setNagadReturn((r) => (r ? { ...r, attempts: (r.attempts || 0) + 1 } : r));
      if ((nagadReturn.attempts || 0) < 30) setTimeout(tick, 3000);
      else setNagadReturn((r) => (r ? { ...r, polling: false } : r));
    };
    setTimeout(tick, 1500);
    return () => { stopped = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nagadReturn?.order_id]);

  const startBdt = async () => {
    setError(null);
    setBdt((n) => ({ ...n, creating: true }));
    try {
      const { data } = await api.post("/wallet/deposit/nagad", { amount_bdt: Number(bdt.amount) });
      setBdt((n) => ({ ...n, creating: false, redirecting: true }));
      window.location.href = data.payment_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || extractError(e) || "Deposit failed. Please try again.";
      setError(String(msg));
      toast.error(String(msg));
      setBdt((n) => ({ ...n, creating: false, redirecting: false }));
    }
  };

  const startUsdt = async () => {
    setError(null);
    setUsdt((c) => ({ ...c, creating: true, invoice: null }));
    try {
      const { data } = await api.post("/wallet/deposit/crypto", { amount_usdt: Number(usdt.amount) });
      setUsdt((c) => ({ ...c, creating: false, invoice: data }));
    } catch (e) {
      const msg = e?.response?.data?.detail || extractError(e) || "Deposit failed";
      setError(String(msg));
      setUsdt((c) => ({ ...c, creating: false }));
    }
  };

  const bdtEnabled = methods?.nagad_p2c && DEPOSIT_ENABLED.BDT;
  const usdtEnabled = methods?.nowpayments && DEPOSIT_ENABLED.USDT;
  const minBdt = methods?.min_deposit_bdt ?? 100;
  const maxBdt = methods?.max_deposit_bdt ?? 100000;
  const minUsdt = methods?.min_deposit_usd ?? 10;

  const openSupport = () => {
    if (typeof window !== "undefined" && typeof window.__openBraGameChat === "function") window.__openBraGameChat();
    else window.location.href = "mailto:support@bragame.com?subject=Deposit%20support";
  };

  return (
    <div data-testid="wallet-deposit">
      <div className="mb-6">
        <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-2">Fund your account</div>
        <h2 className="font-display italic text-3xl text-ink">Deposit</h2>
        <p className="text-sm text-ink-2 mt-2 max-w-xl">
          Choose your currency below. Nagad instantly credits your BDT wallet; USDT-TRC20 tops up your crypto wallet.
        </p>
      </div>

      {/* Currency tabs */}
      <div className="inline-flex bg-base border border-line rounded-xl p-1 mb-5 flex-wrap gap-1" role="tablist" data-testid="deposit-currency-tabs">
        {CURRENCIES.map((c) => {
          const enabled = c.code === "BDT" ? bdtEnabled : c.code === "USDT" ? usdtEnabled : DEPOSIT_ENABLED[c.code];
          // A rail is on the "roadmap" (grey Soon pill) when the currency itself
          // isn't wired yet — INR / PKR. USDT + BDT ARE wired; if their provider
          // is temporarily unconfigured we surface an "unavailable" panel INSIDE
          // the tab instead of labelling the tab itself "Soon".
          const isRoadmap = !DEPOSIT_ENABLED[c.code];
          const active = currency === c.code;
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => selectCurrency(c.code)}
              className={
                "flex items-center gap-2 px-4 py-2 rounded-lg text-xs tracking-[0.15em] uppercase transition " +
                (active
                  ? "bg-elevated text-pink border border-pink/40 shadow-glow-pink"
                  : enabled ? "text-ink-2 hover:text-ink" : "text-ink-3")
              }
              data-testid={`deposit-tab-${c.code}`}
              aria-selected={active}
            >
              {c.code === "BDT" && <Landmark className="w-4 h-4" />}
              {c.code === "USDT" && <CreditCard className="w-4 h-4" />}
              <span className="font-mono">{c.symbol}</span> {c.code}
              {isRoadmap && <Badge variant="muted" className="ml-1 text-[9px]">Soon</Badge>}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2 mb-4" data-testid="deposit-error">
          {error}
        </div>
      )}

      {currency === "BDT" && bdtEnabled && (
        nagadReturn ? (
          <NagadReturnPanel state={nagadReturn} onDismiss={() => setNagadReturn(null)} />
        ) : (
          <BdtForm bdt={bdt} setBdt={setBdt} minBdt={minBdt} maxBdt={maxBdt} onStart={startBdt} />
        )
      )}
      {currency === "BDT" && !bdtEnabled && <UnavailableCard reason="Nagad BDT is currently not configured." onSupport={openSupport} />}

      {currency === "USDT" && usdtEnabled && (
        usdt.invoice ? (
          <UsdtInvoicePanel
            invoice={usdt.invoice}
            onNew={() => setUsdt({ amount: 50, creating: false, invoice: null })}
            onFinalized={async (bonusInfo) => {
              if (bonusInfo?.credited && bonusInfo.amount > 0) {
                toast.success(`Welcome bonus +$${bonusInfo.amount.toFixed(2)} credited!`, { duration: 6000 });
                setBonusFx({ amount: bonusInfo.amount });
                setTimeout(() => setBonusFx(null), 4500);
              }
              await refresh();
            }}
          />
        ) : (
          <UsdtForm usdt={usdt} setUsdt={setUsdt} minUsdt={minUsdt} onStart={startUsdt} />
        )
      )}
      {currency === "USDT" && !usdtEnabled && <UnavailableCard reason="Crypto deposits are currently not configured." onSupport={openSupport} />}

      {(currency === "INR" || currency === "PKR") && <ComingSoonCard code={currency} />}

      <AnimatePresence>
        {bonusFx && <WelcomeBonusOverlay amount={bonusFx.amount} onClose={() => setBonusFx(null)} />}
      </AnimatePresence>
    </div>
  );
}

function ComingSoonCard({ code }) {
  return (
    <Card className="max-w-md" data-testid={`deposit-coming-soon-${code}`}>
      <CardBody>
        <Badge variant="muted" className="mb-3">Coming soon</Badge>
        <div className="font-display italic text-2xl text-ink mb-2">{code} deposits are on the roadmap</div>
        <p className="text-sm text-ink-2 mb-4">
          {code} is playable in-game — you can bet in {code} from any of your buckets. Deposit rails aren't wired
          yet. To fund your account today, try USDT (TRC20) or BDT via Nagad.
        </p>
        <div className="text-[10px] tracking-widest uppercase text-ink-3">Switch to USDT or BDT above ↑</div>
      </CardBody>
    </Card>
  );
}

function UnavailableCard({ reason, onSupport }) {
  return (
    <Card className="max-w-xl" data-testid="deposits-unavailable">
      <CardBody>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl border border-line bg-base flex items-center justify-center shrink-0">
            <LifeBuoy className="w-6 h-6 text-pink" />
          </div>
          <div className="flex-1 min-w-0">
            <Badge variant="new" className="mb-2">Temporarily unavailable</Badge>
            <div className="font-display italic text-2xl text-gold mb-2">{reason}</div>
            <div className="mt-2">
              <Button onClick={onSupport} variant="primary" size="lg" data-testid="deposits-support-btn">
                <MessageSquare className="w-4 h-4 mr-2" /> Contact support
              </Button>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function BdtForm({ bdt, setBdt, minBdt, maxBdt, onStart }) {
  const invalid = Number(bdt.amount) < minBdt || Number(bdt.amount) > maxBdt;
  return (
    <Card className="max-w-md" data-testid="nagad-form">
      <CardBody>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg border border-pink/40 bg-pink/10 flex items-center justify-center">
            <Landmark className="w-5 h-5 text-pink" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-2">Instant · Bangladesh</div>
            <div className="text-sm text-ink">Nagad P2C · via Payfly</div>
          </div>
        </div>
        <AmountPicker
          amount={bdt.amount}
          setAmount={(v) => setBdt((n) => ({ ...n, amount: v }))}
          chips={BDT_CHIPS}
          min={minBdt} max={maxBdt}
          currencyPrefix="৳"
          label={`Amount (BDT · min ৳${minBdt.toLocaleString()})`}
          testid="nagad-amount"
        />
        <div className="text-[10px] text-ink-3 mb-4 font-mono">
          ৳{Number(bdt.amount || 0).toLocaleString()} will be credited to your BDT balance
        </div>
        <Button
          onClick={onStart}
          disabled={bdt.creating || bdt.redirecting || invalid}
          variant="primary" size="lg"
          loading={bdt.creating || bdt.redirecting}
          className="w-full"
          data-testid="nagad-continue-btn"
        >
          <Landmark className="w-4 h-4 mr-2" />
          {bdt.redirecting ? "Redirecting to Nagad…" : bdt.creating ? "Creating checkout…" : `Pay ৳${Number(bdt.amount || 0).toLocaleString()} with Nagad`}
        </Button>
      </CardBody>
    </Card>
  );
}

function UsdtForm({ usdt, setUsdt, minUsdt, onStart }) {
  const invalid = Number(usdt.amount) < minUsdt || Number(usdt.amount) > 10000;
  return (
    <Card className="max-w-md" data-testid="crypto-form">
      <CardBody>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg border border-pink/40 bg-pink/10 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-pink" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-ink-2">Crypto · TRC20</div>
            <div className="text-sm text-ink">USDT via NOWPayments</div>
          </div>
        </div>
        <AmountPicker
          amount={usdt.amount}
          setAmount={(v) => setUsdt((c) => ({ ...c, amount: v }))}
          chips={USDT_CHIPS}
          min={minUsdt} max={10000}
          currencyPrefix="$"
          label={`Amount (USDT · min ${formatAmount(minUsdt, "USDT")})`}
          testid="crypto-amount"
        />
        <Button
          onClick={onStart}
          disabled={usdt.creating || invalid}
          variant="primary" size="lg" loading={usdt.creating}
          className="w-full" data-testid="crypto-continue-btn"
        >
          <Coins className="w-4 h-4 mr-2" />
          {usdt.creating ? "Generating invoice…" : `Deposit USDT TRC20 · ${formatAmount(usdt.amount, "USDT")}`}
        </Button>
      </CardBody>
    </Card>
  );
}

function UsdtInvoicePanel({ invoice, onNew, onFinalized }) {
  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };
  useEffect(() => {
    let stopped = false;
    let attempts = 0;
    const tick = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/wallet/deposit/${invoice.deposit_id}/status`);
        if (data.status === "confirmed") {
          onFinalized?.({ credited: data.welcome_bonus_credited, amount: data.welcome_bonus_amount_usd });
          return;
        }
        if (data.status === "failed" || data.status === "expired") return;
      } catch (e) {
        console.debug("[WalletDeposit] crypto poll transient:", e);
      }
      if (attempts < 60) setTimeout(tick, 5000);
    };
    setTimeout(tick, 5000);
    return () => { stopped = true; };
  }, [invoice.deposit_id, onFinalized]);

  return (
    <Card className="max-w-md" data-testid="crypto-invoice">
      <CardBody>
        <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-2">Send exactly</div>
        <div className="font-display italic gold-text text-3xl mb-1" data-testid="crypto-amount-display">
          {invoice.crypto_amount} {invoice.crypto_currency.toUpperCase()}
        </div>
        <div className="text-[10px] text-ink-3 mb-4">to the TRC20 address below</div>
        <div className="border border-line rounded-lg p-3 font-mono text-xs break-all text-ink flex items-center gap-2 bg-base" data-testid="crypto-address">
          <span className="flex-1">{invoice.crypto_address}</span>
          <button onClick={() => copy(invoice.crypto_address)} className="text-pink hover:text-gold transition" data-testid="crypto-copy-btn">
            <Copy className="w-4 h-4" />
          </button>
        </div>
        {invoice.invoice_url && (
          <a href={invoice.invoice_url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-[10px] tracking-[0.3em] uppercase text-pink hover:underline" data-testid="crypto-invoice-link">
            Open provider invoice →
          </a>
        )}
        <button onClick={onNew} className="mt-4 inline-flex items-center gap-1 text-[10px] tracking-[0.3em] uppercase text-ink-2 hover:text-pink transition" data-testid="crypto-new-btn">
          <ArrowLeft className="w-3 h-3" /> Start over
        </button>
      </CardBody>
    </Card>
  );
}

function NagadReturnPanel({ state, onDismiss }) {
  const isPending = state.polling;
  const isCredited = state.credited;
  return (
    <Card className="max-w-md" data-testid="nagad-return">
      <CardBody>
        <div className="text-[10px] tracking-[0.3em] uppercase text-ink-2 mb-2">Nagad deposit</div>
        {isPending && (
          <>
            <div className="font-display italic text-2xl text-gold mb-2" data-testid="nagad-return-pending">Confirming your payment…</div>
            <p className="text-sm text-ink-2 mb-4">Usually 5–20 seconds. Balance will update automatically.</p>
            <div className="flex gap-2 items-center text-xs text-ink-3">
              <span className="inline-block w-2 h-2 rounded-full bg-pink animate-pulse" />
              Order · <span className="font-mono">{state.order_id.slice(0, 8)}…</span>
            </div>
          </>
        )}
        {!isPending && isCredited && (
          <>
            <div className="font-display italic gold-text text-3xl mb-2" data-testid="nagad-return-credited">
              Credited: ৳{(state.actual_received_amount_bdt || state.amount_bdt || 0).toLocaleString()} BDT
            </div>
            <p className="text-xs text-ink-3 mb-4">Your BDT balance was updated instantly.</p>
            <Button onClick={onDismiss} variant="primary" size="lg" data-testid="nagad-return-dismiss">Make another deposit</Button>
          </>
        )}
        {!isPending && !isCredited && (
          <>
            <div className="font-display italic text-2xl text-danger mb-2" data-testid="nagad-return-failed">Deposit not confirmed</div>
            <p className="text-sm text-ink-2 mb-4">
              Status: <span className="font-mono">{state.status || "unknown"}</span>. No funds were credited. Try again below.
            </p>
            <Button onClick={onDismiss} variant="primary" size="lg" data-testid="nagad-return-dismiss">Try again</Button>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function WelcomeBonusOverlay({ amount, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-auto"
      style={{ background: "rgba(11,14,30,0.75)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="welcome-bonus-overlay"
    >
      <motion.div
        initial={{ scale: 0.6, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="relative z-10 max-w-md px-8 py-10 rounded-2xl border border-gold/60 bg-elevated text-center shadow-glow-gold"
      >
        <Gift className="w-10 h-10 text-gold mx-auto mb-4" />
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold mb-2">Welcome bonus</div>
        <div className="font-display italic text-5xl gold-text mb-3" data-testid="welcome-bonus-amount">
          +${amount.toFixed(2)}
        </div>
        <p className="text-sm text-ink-2">Credited to your USDT wallet. No wagering. Yours to keep.</p>
      </motion.div>
    </motion.div>
  );
}

function AmountPicker({ amount, setAmount, chips, min, max, currencyPrefix, label, testid }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] tracking-[0.25em] uppercase text-ink-2 mb-2">{label}</div>
      <div className="grid grid-cols-5 gap-2 mb-3">
        {chips.map((c) => (
          <button
            key={c}
            onClick={() => setAmount(c)}
            className={
              "py-2 text-xs font-mono border rounded-lg transition " +
              (Number(amount) === c ? "border-pink text-pink bg-pink/10" : "border-line text-ink-2 hover:text-ink hover:border-ink-2")
            }
            data-testid={`${testid}-chip-${c}`}
          >
            {currencyPrefix}{c.toLocaleString?.() || c}
          </button>
        ))}
      </div>
      <Input
        type="number"
        min={min} max={max} step="any"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        data-testid={`${testid}-input`}
        inputClassName="font-mono"
      />
    </div>
  );
}
